import { Box, Snackbar, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TemplateFieldsSection from '../components/common/TemplateFieldsSection/TemplateFieldsSection';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import { DisableContext } from '../contexts/DisableContextProvider';
import { useCreateFormTemplate, useGetFormTemplateById, useUpdateFormTemplate } from '../services/react-query/form_templates';
import { inputContainerStyling, inputDividerStyling } from '../styles/sxStyling';
import { FormTemplate } from '../types/FormTemplate';
import { UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import { errorPopulator } from '../utility/inputUtils';
import TagsSection from '../components/common/TagsSection/TagsSection';
import AccordionSection from '../components/AccordionSection';
import { TemplateField } from '../types/FieldCommon';
import _ from 'lodash';
import AttachmentsSection from '../components/attachments/AttachmentsSection';
import { AttachmentOwnerType } from '../types/Attachment';

export default function FormTemplateItem() {
  const { uuid } = useParams();
  const [formTemplate, setFormTemplate] = useState<FormTemplate | null>(null);
  const [saved, setSaved] = useState<boolean>(false);

  const { data, isLoading } = useGetFormTemplateById({ uuid });

  const { mutate: createFormTemplate, isPending: isCreateFormTemplateMutation } = useCreateFormTemplate({ onSuccessCallback: () => null });
  const { mutate: updateFormTemplate, isPending: isUpdateFormTemplateMutation } = useUpdateFormTemplate({
    uuid,
    onSuccessCallback: () => setSaved(true),
  });

  useEffect(() => {
    if (data) setFormTemplate(data);
  }, [data]);

  const { disabled } = useContext(DisableContext);

  if (isLoading || formTemplate === null) return <Loading />;

  const handleSave = () => {
    if (uuid === 'new') {
      createFormTemplate(formTemplate);
    } else {
      updateFormTemplate(formTemplate);
    }
  };

  // TODO add handling for required and desctiption values
  const setFields = (templateFields: TemplateField[]) => setFormTemplate({ ...formTemplate, fields: _.map(templateFields, (field) => ({ ...field, required: false })) });

  return (
    <Box>
      <ItemHeader
        title={`${uuid === 'new' ? 'Create' : 'Update'} Form Template`}
        onSave={handleSave}
        disableActions={Boolean(isCreateFormTemplateMutation || isUpdateFormTemplateMutation)}
      />
      <AccordionSection title="Basic Information" collapseId={UserSettingsCollapseKeys.FORM_TEMPLATES_ITEM_INFORMATION}>
        <Box sx={inputContainerStyling}>
          <Box sx={inputDividerStyling}>
            <TextField
              id="name"
              label="Name"
              autoFocus
              onChange={(event) => setFormTemplate({ ...formTemplate, name: event.target.value })}
              value={formTemplate.name}
              disabled={disabled}
              {...errorPopulator(formTemplate, 'name')}
            />
          </Box>
          <Box sx={inputDividerStyling}>
            <TextField
              id="description"
              label="Description"
              onChange={(event) => setFormTemplate({ ...formTemplate, description: event.target.value })}
              value={formTemplate.description}
              disabled={disabled}
              {...errorPopulator(formTemplate, 'description')}
            />
          </Box>
        </Box>
      </AccordionSection>

      <TemplateFieldsSection fields={formTemplate.fields} setFields={setFields} collapseId={UserSettingsCollapseKeys.FORM_TEMPLATES_ITEM_FIELDS} />

      <TagsSection
        tags={formTemplate.tags}
        collapseId={UserSettingsCollapseKeys.FORM_TEMPLATES_ITEM_TAGS}
        setTags={(tags) => setFormTemplate({ ...formTemplate, tags })}
      />

      <AttachmentsSection collapseId={UserSettingsCollapseKeys.FORM_TEMPLATES_ITEM_ATTACHMENTS} ownerType={AttachmentOwnerType.FORM_TEMPLATE} ownerUuid={uuid ?? ''} />

      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Box>
  );
}
